/**
 * LeadOpsStatusRibbon — summary filter cards matching Lead Ops mock.
 */

import React, { useMemo } from "react";
import { Box, Typography, Paper } from "@mui/material";
import {
  FiberNew,
  RequestQuote,
  HourglassTop,
  LocalShipping,
  Send,
  EventAvailable,
  ReportProblem,
} from "@mui/icons-material";
import {
  OPERATIONAL_BUCKETS,
  computeLeadOperationalSummary,
  getLeadOperationalLabel,
} from "../services/leadOperationalStatus.js";

const BUCKET_META = {
  new: { color: "#1976d2", Icon: FiberNew },
  ready_to_quote: { color: "#2e7d32", Icon: RequestQuote },
  awaiting_customer: { color: "#f9a825", Icon: HourglassTop },
  awaiting_provider: { color: "#ef6c00", Icon: LocalShipping },
  quote_sent: { color: "#7b1fa2", Icon: Send },
  booked: { color: "#4527a0", Icon: EventAvailable },
  needs_review: { color: "#c62828", Icon: ReportProblem },
};

const LeadOpsStatusRibbon = ({
  leads = [],
  activeBucket = null,
  onBucketClick,
}) => {
  const summary = useMemo(
    () => computeLeadOperationalSummary(leads),
    [leads]
  );

  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: {
          xs: "repeat(2, 1fr)",
          sm: "repeat(3, 1fr)",
          md: "repeat(4, 1fr)",
          lg: "repeat(7, 1fr)",
        },
        gap: 1.5,
        mb: 2.5,
      }}
      data-testid="lead-ops-status-ribbon"
    >
      {OPERATIONAL_BUCKETS.map((bucket) => {
        const count = summary[bucket] || 0;
        const selected = activeBucket === bucket;
        const meta = BUCKET_META[bucket] || BUCKET_META.new;
        const Icon = meta.Icon;
        return (
          <Paper
            key={bucket}
            elevation={0}
            onClick={
              onBucketClick
                ? () => onBucketClick(selected ? null : bucket)
                : undefined
            }
            sx={{
              p: 1.5,
              cursor: onBucketClick ? "pointer" : "default",
              borderRadius: 2,
              border: "1px solid",
              borderColor: selected ? meta.color : "divider",
              backgroundColor: selected
                ? `${meta.color}14`
                : "background.paper",
              transition: "border-color 0.15s, background-color 0.15s",
              "&:hover": onBucketClick
                ? { borderColor: meta.color, backgroundColor: `${meta.color}0A` }
                : undefined,
            }}
          >
            <Box display="flex" alignItems="center" gap={1} mb={0.5}>
              <Box
                sx={{
                  width: 28,
                  height: 28,
                  borderRadius: "50%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  backgroundColor: `${meta.color}22`,
                  color: meta.color,
                }}
              >
                <Icon sx={{ fontSize: 16 }} />
              </Box>
              <Typography
                variant="h6"
                sx={{ fontWeight: 700, lineHeight: 1.1, fontSize: "1.15rem" }}
              >
                {count}
              </Typography>
            </Box>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ fontWeight: 600, displaySize: "0.7rem" }}
            >
              {getLeadOperationalLabel(bucket)}
            </Typography>
          </Paper>
        );
      })}
    </Box>
  );
};

export default LeadOpsStatusRibbon;
